import React, { useState, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
} from "@mui/material";
import { AnimalForm } from "./animalForm/animalForm";
import Dropzone, { Accept } from "react-dropzone";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { FaHorse } from "react-icons/fa";

interface AnimalFormDialogProps {
  open: boolean;
  onClose: () => void;
  formData: AnimalForm;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSubmit: () => void;
  onFileChange: (files: FileList | null) => void;
  onSelectChange: (value: string) => void;
}

const AnimalFormDialog: React.FC<AnimalFormDialogProps> = ({
  open,
  onClose,
  formData,
  onChange,
  onSubmit,
  onFileChange,
  onSelectChange,
}) => {
  const [previewImages, setPreviewImages] = useState<string[]>([]);

  const handleDrop = useCallback(
    (acceptedFiles: File[]) => {
      previewImages.forEach(URL.revokeObjectURL);

      const filePreviews = acceptedFiles.map((file) =>
        URL.createObjectURL(file)
      );

      setPreviewImages(filePreviews);

      const dataTransfer = new DataTransfer();
      acceptedFiles.forEach((file) => dataTransfer.items.add(file));
      onFileChange(dataTransfer.files);
    },
    [previewImages, onFileChange]
  );

  const acceptedFileTypes: Accept = {
    "image/*": [".jpeg", ".png", ".jpg"],
  };

  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Agregar Nuevo Animal</DialogTitle>
      <DialogContent>
        <Dropzone onDrop={handleDrop} accept={acceptedFileTypes}>
          {({ getRootProps, getInputProps }) => (
            <section>
              <div
                {...getRootProps({ className: "dropzone" })}
                style={{
                  border: "2px dashed #cccccc",
                  padding: "20px",
                  textAlign: "center",
                  cursor: "pointer",
                }}
              >
                <input {...getInputProps()} />
                <p>
                  Arrastra y suelta algunos archivos aquí, o haz clic para
                  seleccionar archivos
                </p>
              </div>
            </section>
          )}
        </Dropzone>
        {previewImages.length > 1 ? (
          <Slider {...settings}>
            {previewImages.map((src) => (
              <div key={src}>
                <img
                  src={src}
                  alt="Vista previa"
                  style={{ width: "100%", height: "auto" }}
                />
              </div>
            ))}
          </Slider>
        ) : (
          previewImages.length > 0 && (
            <img
              src={previewImages[0]}
              alt="Vista previa"
              style={{
                width: "100%",
                maxHeight: "300px",
                objectFit: "contain",
                margin: "0 auto",
                padding: "20px",
              }}
            />
          )
        )}
        <TextField
          autoFocus
          margin="dense"
          name="name"
          label="Nombre"
          type="text"
          fullWidth
          variant="outlined"
          value={formData.name}
          onChange={onChange}
        />
        <FormControl fullWidth margin="dense">
          <InputLabel id="species-label">Especie</InputLabel>
          <Select
            labelId="species-label"
            name="species"
            value={formData.specie}
            onChange={(e) => onSelectChange(e.target.value)}
            variant="outlined"
          >
            <MenuItem value="cattle">
              <FaHorse style={{ marginRight: 8 }} /> Ganado
            </MenuItem>
            <MenuItem value="sheep">
              <FaHorse style={{ marginRight: 8 }} /> Oveja
            </MenuItem>
            <MenuItem value="donkey">
              <FaHorse style={{ marginRight: 8 }} /> Burro
            </MenuItem>
          </Select>
        </FormControl>
        <TextField
          margin="dense"
          name="breed"
          label="Raza"
          fullWidth
          variant="outlined"
          value={formData.breed}
          onChange={onChange}
        />
        <TextField
          margin="dense"
          name="age"
          label="Edad (años)"
          type="number"
          fullWidth
          variant="outlined"
          value={formData.age}
          onChange={onChange}
        />
        <TextField
          margin="dense"
          name="description"
          label="Descripción"
          type="text"
          fullWidth
          variant="outlined"
          multiline
          rows={4}
          value={formData.description}
          onChange={onChange}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button onClick={onSubmit} variant="contained">
          Agregar Animal
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AnimalFormDialog;
